/**
 * Which legal form may be taxed which way, for Settings and signup (0094;
 * PLAN_tax_treatments_20261021 §1). A copy of `apps/api/src/business-type.ts`'s
 * matrix, held to it by `test/business-types.test.ts`, which imports the API's
 * module and compares: the screen offers what the API accepts, no more.
 *
 * THE RECOMMENDATION IS THE IRS DEFAULT, never advice: what the business is
 * unless it filed an election. A status the law allows and the app does not
 * build yet is not offered at all (round 181's TX-2), and where the default is
 * one of those, nothing is preselected and `defaultNotBuilt` says why.
 */

import type { BusinessType } from '../api';

export type LegalForm = BusinessType['legalForm'];
export type TaxStatus = BusinessType['taxStatus'];
export type ChosenLegalForm = Exclude<LegalForm, 'llc'>;

/** Federal law's matrix, each form's default classification first. */
export const TAX_STATUSES_BY_FORM: Record<ChosenLegalForm, readonly TaxStatus[]> = {
  sole_proprietorship: ['disregarded_entity'],
  single_member_llc: ['disregarded_entity', 's_corporation', 'c_corporation'],
  multi_member_llc: ['partnership', 's_corporation', 'c_corporation'],
  partnership: ['partnership', 's_corporation', 'c_corporation'],
  corporation: ['c_corporation', 's_corporation'],
  nonprofit_corporation: ['tax_exempt'],
  trust: ['tax_exempt'],
};

export const LEGAL_FORMS = Object.keys(TAX_STATUSES_BY_FORM) as ChosenLegalForm[];

/** The statuses whose rules are built; 0095 added `c_corporation` and 0096 `partnership`, so every one the law allows. */
export const BUILT_TAX_STATUSES: readonly TaxStatus[] = ['disregarded_entity', 'partnership', 's_corporation', 'c_corporation', 'tax_exempt'];

export const LEGAL_FORM_NAMES: Record<LegalForm, string> = {
  sole_proprietorship: 'Sole Proprietorship',
  single_member_llc: 'Single-Member LLC',
  multi_member_llc: 'Multi-Member LLC',
  partnership: 'Partnership',
  corporation: 'Corporation',
  nonprofit_corporation: 'Non-Profit Corporation',
  trust: 'Charitable Trust',
  // The sentence under the type asks its members; the choice itself stays short enough to read.
  llc: 'LLC',
};

export const TAX_STATUS_NAMES: Record<TaxStatus, string> = {
  disregarded_entity: 'Taxed With The Owner (Disregarded Entity)',
  partnership: 'Partnership',
  s_corporation: 'S Corporation',
  c_corporation: 'C Corporation',
  tax_exempt: 'Tax-Exempt, 501(c)(3)',
};

export function recommendedTaxStatus(form: ChosenLegalForm): TaxStatus {
  return TAX_STATUSES_BY_FORM[form][0]!;
}

/** What a form may be recorded with today: the law's statuses, less those not built. */
export function offeredTaxStatuses(form: ChosenLegalForm): Array<BusinessType['taxStatus']> {
  return TAX_STATUSES_BY_FORM[form].filter((status): status is BusinessType['taxStatus'] => BUILT_TAX_STATUSES.includes(status));
}

/** The status to preselect: the default where it is built, and none where it is not (TX-2). */
export function preselectedTaxStatus(form: ChosenLegalForm): BusinessType['taxStatus'] | null {
  const recommended = recommendedTaxStatus(form);
  return offeredTaxStatuses(form).find((status) => status === recommended) ?? null;
}

const DEFAULT_PHRASE: Partial<Record<TaxStatus, string>> = {
  partnership: 'taxed as a partnership',
  c_corporation: 'taxed as a C corporation',
};

/**
 * Where the IRS default is not built yet, the sentence that takes the
 * preselection's place (the plan's §1.3): for a multi-member LLC, "A
 * multi-member LLC is taxed as a partnership unless it elected otherwise, and
 * partnership books are not supported yet." It names no election: since 0095
 * such a form offers two, S Corporation (Form 2553) and C Corporation (Form
 * 8832), and `electionFor` names the one chosen (round 183's TT-1, its other
 * way).
 */
export function defaultNotBuilt(form: ChosenLegalForm): string | null {
  if (preselectedTaxStatus(form) !== null) return null;
  const recommended = recommendedTaxStatus(form);
  const name = LEGAL_FORM_NAMES[form];
  const books = recommended === 'partnership' ? 'partnership books are' : 'C corporation books are';
  return `A ${name.toLowerCase().replace('llc', 'LLC')} is ${DEFAULT_PHRASE[recommended]} unless it elected otherwise, and ${books} not supported yet.`;
}

/** The election a choice other than the default means was filed, or null for the default. */
export function electionFor(form: ChosenLegalForm, status: TaxStatus): string | null {
  if (status === recommendedTaxStatus(form)) return null;
  if (status === 's_corporation') return 'Choose this only if the business filed Form 2553 to be taxed as an S corporation.';
  if (status === 'c_corporation') return 'Choose this only if the business filed Form 8832 to be taxed as a corporation.';
  return null;
}
